/** @format */
import { useDispatch } from 'react-redux';
import { useCallback, useEffect, useState } from 'react';
import { REQUEST_STATE } from '../../../helpers/RequestHelper';
import { ItemLoaders } from '../../../core/loaders/ItemLoaders';
import { FormGeneralHelper } from '../../../helpers/FormGeneral';
import Order from '../../../interfaces/forms/Order';
import Filter from '../../../interfaces/forms/Filter';
import { PrefixUploadCollectionInterface } from '../interfaces/PrefixUploadInterface';
import { configDefault } from '../../../core/config';
import { prefixesConstants } from '../actions';

export interface PrefixUploadCollectionHookResponseInterface {
    prefixUploadCollection: PrefixUploadCollectionInterface | null;
    refreshCollectionCallback: () => void;
    getCollectionCallback: (uri: string) => void;
    state: number;
}

export const usePrefixUploadCollection = (filters: Array<Filter> = [], orders: Array<Order> = []): PrefixUploadCollectionHookResponseInterface => {
    const dispatch = useDispatch();
    const [responseState, setResponseState] = useState<number>(REQUEST_STATE.IDLE);
    const [collection, setCollection] = useState<PrefixUploadCollectionInterface | null>(null);

    const getCollectionCallback = useCallback(
        (uri: string) => {
            setResponseState(REQUEST_STATE.PENDING);
            const itemLoader = new ItemLoaders(dispatch);
            itemLoader.getListWithCallback(
                FormGeneralHelper.getByUri(uri),
                prefixesConstants.PREFIX_UPLOAD_LIST,
                filters,
                orders,
                {
                    onSuccess: (data: PrefixUploadCollectionInterface) => {
                        setCollection(data);
                        setResponseState(REQUEST_STATE.IDLE);
                    },
                    onError: (error: any) => {
                        setCollection(null);
                        setResponseState(REQUEST_STATE.ERROR);
                    },
                },
                { propagateDataToCallback: true, dispatchAfterCallback: false }
            );
        },
        [dispatch, filters, orders]
    );

    useEffect(() => {
        if (responseState !== REQUEST_STATE.PENDING && responseState !== REQUEST_STATE.ERROR && !collection) {
            getCollectionCallback(FormGeneralHelper.getPath('/prefix_uploads', configDefault.PLATFORM_API_PREFIX));
        }
    }, [dispatch, responseState, collection, filters, orders, getCollectionCallback]);

    const resetState = (): void => {
        setCollection(null);
        setResponseState(REQUEST_STATE.IDLE);
    };

    return {
        prefixUploadCollection: collection,
        refreshCollectionCallback: resetState,
        getCollectionCallback: getCollectionCallback,
        state: responseState,
    };
};
